import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState
} from 'react';
import { PropsCommunity } from 'src/pages/CommunityDetails/Posts/types';
import { ResponseGetOnboarding } from 'src/services/onboarding';
import { PermissionActions } from 'src/services/posts';
import { filterPermission } from 'src/utils/user';
import { PERMISSIONS_ACTIONS } from 'src/utils/user/constant';

export type OnboardingContextProps = {
  onboardingSteps: ResponseGetOnboarding;
  setOnboardingSteps: Dispatch<SetStateAction<ResponseGetOnboarding>>;
  openOnboarding: boolean;
  setOpenOnboarding: Dispatch<SetStateAction<boolean>>;
  hasUserPermissionOboarding: boolean;
  community: PropsCommunity | null;
  setCommunity: Dispatch<SetStateAction<PropsCommunity | null>>;
};

export const OnboardingContext = createContext({} as OnboardingContextProps);

export type OnboardingProviderProps = {
  children: ReactNode;
};

const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const [onboardingSteps, setOnboardingSteps] = useState<ResponseGetOnboarding>({
    communityAdmin: {
      communityEdit: {
        communityName: false,
        communityImage: false
      },
      communityMember: {
        communityModerators: false
      },
      communityPostCreate: {
        communityFirstPost: false
      },
      communityShare: {
        communityShare: false
      }
    },
    progress: 0
  });
  const [openOnboarding, setOpenOnboarding] = useState<boolean>(false);
  const [community, setCommunity] = useState<PropsCommunity | null>(null);

  const hasUserPermissionOboarding = useMemo(() => {
    if (community?.permissions?.actions) {
      return Boolean(
        filterPermission({
          specificPermission:
            PERMISSIONS_ACTIONS.COMMUNITY_ADMIN_ONBOARDING_VIEW as PermissionActions,
          permissionsUserList: community.permissions.actions as PermissionActions[]
        })
      );
    }

    return false;
  }, [community?.permissions]);

  return (
    <OnboardingContext.Provider
      value={{
        onboardingSteps,
        setOnboardingSteps,
        openOnboarding,
        setOpenOnboarding,
        hasUserPermissionOboarding,
        community,
        setCommunity
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

function useOnboarding() {
  const context = useContext(OnboardingContext);

  if (!context) {
    throw new Error('useEmbedLayout must be used within a EmbedLayoutProvider');
  }

  return context;
}

export { OnboardingProvider, useOnboarding };
